'use client';

import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowRight, X as XIcon, Settings2 } from 'lucide-react';
import { getQuickConditionsForSource } from '../constants/edgeConditions';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MarkerType,
  BackgroundVariant,
  MiniMap,
  useReactFlow,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  ConnectionLineType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FlowStep, StepType, FunnelNode, FunnelEdge, nodeToFlowStep, flowStepToNode } from '../types';
import { FlowStepNode } from './FlowStepNode';
import { QuickStepSelectionDialog } from './QuickStepSelectionDialog';
import { createNewStep } from '../utils/helpers';
import type { ConditionValue } from '../types/conditions';
import { generateEdgeId } from '../utils/funnelHelpers';
import { HANDLE_IDS } from '../constants/handles';
import { screenshotKey } from '../hooks/useInstanceScreenshotsForFunnel';
import type { IntegrationProvider } from '@/lib/integrations-constants';
import { cn } from '@/lib/utils';

/**
 * Step types that trigger an inngest screenshot capture on save. Mirrors
 * SCREENSHOTTABLE_STEP_TYPES in src/app/useCases/funnels/saveFunnel.ts —
 * if you add a new routable step type there, add it here too so the
 * "Capturing..." spinner fires on the matching step blocks.
 */
const ROUTABLE_CAPTURE_TYPES = new Set<string>([
  'checkout',
  'thankyou',
  'offer',
  'upsell',
  'downsell',
  'landing',
]);

/**
 * Does this node already have a usable preview to show? Mirrors the
 * resolution order in FlowStepNode's `screenshot` / `hasAnyScreenshot`
 * memos: a captured `previewScreenshot` (or per-variant capture) OR a
 * deployment screenshot fallback keyed by instanceId. The "Capturing..."
 * spinner must treat ALL of these as "has preview" — otherwise a step with
 * a perfectly good Design thumbnail (but no `previewScreenshot.url` written
 * yet) would spin forever, since `previewScreenshot.url` is only ever
 * written by the inngest capture job and that job silently no-ops when the
 * step has no deployed `config.url` or the capture fails.
 */
function nodeHasUsablePreview(
  node: any,
  instanceScreenshots?: Record<string, string>,
): boolean {
  const variants: any[] = Array.isArray(node?.variants) ? node.variants : [];
  if (variants.length > 0) {
    const vs = node?.metadata?.variantScreenshots || {};
    return variants.some((variant: any) => {
      if (vs[variant?.id]?.url) return true;
      const vId = variant?.config?.instanceId;
      const vPath = variant?.config?.metadata?.internalPath
        ?? variant?.config?.pagePath
        ?? variant?.config?.path;
      return !!(vId && (instanceScreenshots?.[screenshotKey(vId, vPath)] || instanceScreenshots?.[vId]));
    });
  }
  if (node?.metadata?.previewScreenshot?.url) return true;
  const instanceId = node?.config?.instanceId;
  const pagePath = node?.config?.metadata?.internalPath
    ?? node?.config?.pagePath
    ?? node?.config?.path;
  return !!(instanceId && (instanceScreenshots?.[screenshotKey(instanceId, pagePath)] || instanceScreenshots?.[instanceId]));
}

/**
 * Has this node's preview capture errored (the inngest job ran and failed)?
 * A capture error is a terminal "stop waiting" signal — the step block should
 * drop the "Capturing…" spinner and show its error/retry state instead. For
 * split steps, any errored variant counts.
 */
function nodeHasCaptureError(node: any): boolean {
  if (node?.metadata?.previewScreenshotError) return true;
  const variants: any[] = Array.isArray(node?.variants) ? node.variants : [];
  if (variants.length > 0) {
    const ve = node?.metadata?.variantScreenshotErrors || {};
    return variants.some((variant: any) => !!ve[variant?.id]);
  }
  return false;
}

/**
 * Helper function to create a FunnelEdge with proper handle configuration
 * @param source - Source node ID
 * @param target - Target node ID
 * @param sourceHandle - Handle ID on source node (default: 'right')
 * @param targetHandle - Handle ID on target node (default: 'left')
 * @returns FunnelEdge object with all required fields
 */
const createEdge = (
  source: string,
  target: string,
  sourceHandle: string = HANDLE_IDS.RIGHT,
  targetHandle: string = HANDLE_IDS.LEFT,
  conditions?: FunnelEdge['conditions'],
): FunnelEdge => ({
  id: generateEdgeId(source, target),
  source,
  target,
  sourceHandle,
  targetHandle,
  ...(conditions ? { conditions } : {}),
});

/**
 * Default condition for a new edge, by source step type.
 *
 * Edges leaving a checkout default to "payment.success" so customers are never
 * routed forward (e.g. to an upsell) on a declined payment unless the merchant
 * explicitly opts out (TAG-1084). The quick-condition popover / edge editor can
 * still override or clear it.
 */
const DEFAULT_CHECKOUT_CONDITION = 'payment.success';
const defaultEdgeConditions = (sourceType?: string): FunnelEdge['conditions'] | undefined =>
  sourceType === 'checkout' ? { when: DEFAULT_CHECKOUT_CONDITION } : undefined;

interface FunnelFlowCanvasProps {
  // 🎯 Nodes and Edges (Primary Source of Truth)
  nodes?: FunnelNode[];
  edges?: FunnelEdge[];
  onUpdateNodes?: (nodes: FunnelNode[]) => void;
  onUpdateEdges?: (edges: FunnelEdge[]) => void;

  // 🔄 Legacy: Support old steps format (optional/deprecated)
  steps?: FlowStep[];
  onUpdateSteps?: (steps: FlowStep[]) => void;

  // Common props
  selectedStepId: string | null;
  selectedEdgeId?: string | null;
  onSelectStep: (stepId: string | null) => void;
  onEditStep?: (stepId: string, tab?: string) => void;
  onEditConfig?: (stepId: string) => void;
  onEditEdge?: (edgeId: string | null) => void;
  onPreviewStep?: (stepId: string) => void;
  onRetryScreenshot?: (stepId: string, variantId?: string) => void;
  onGetCheckoutLink?: (stepId: string) => void;
  onTransformToSplit?: (stepId: string) => void;
  onDuplicateDesign?: (stepId: string) => void;
  onDeleteStep?: (stepId: string) => void;
  onStepCreated?: (stepId: string) => void;
  onStepLabelChange?: (stepId: string, newLabel: string) => void;
  stepAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number | null; // % who progressed to next step (null if non-calculable)
      progressedToNext: number; // Count who progressed to next step
      realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
  variantAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number | null; // % who progressed to next step (null if non-calculable)
      progressedToNext: number; // Count who progressed to next step
      realtimeVisitors: number;
      conversionValue: number;
      conversionValueUnit: string;
      avgConversionValue: number;
      hasNextStep: boolean;
    }
  >;
  readOnly?: boolean;
  showPreviews?: boolean; // Global toggle for screenshot previews
  waitingForScreenshotStepId?: string | null; // Step ID that's currently waiting for screenshot capture
  /**
   * Global "screenshots are still being captured for this funnel" flag.
   * When true, every step that doesn't yet have a previewScreenshot will
   * display the capturing spinner (instead of a "No preview available"
   * placeholder). Set on freshly-created funnels via the `?tour=setup`
   * URL param so users see what's happening while Inngest jobs run.
   */
  isPollingInitialScreenshots?: boolean;
  storeType?: IntegrationProvider;
  connectedProviders?: IntegrationProvider[];
  stepsMissingDesign?: Array<{ id: string; variantId?: string }>;
  integrationUrls?: Record<string, string>; // Provider -> store URL from integration credentials (e.g. { woocommerce: "http://...", shopify: "mystore.myshopify.com" })
  instanceScreenshots?: Record<string, string>; // instanceId -> screenshot URL (from deployment, fallback when previewScreenshot not yet captured)
}

// Workspace area background - shows the accessible zone
const WorkspaceArea = () => {
  const width = CANVAS_BOUNDS.MAX_X - CANVAS_BOUNDS.MIN_X + 400;
  const height = CANVAS_BOUNDS.MAX_Y - CANVAS_BOUNDS.MIN_Y + 220;

  return (
    <div
      className="pointer-events-none rounded-xl border-2 border-dashed border-slate-300/60 bg-white/50 dark:border-slate-700/60 dark:bg-slate-900/30"
      style={{ width, height }}
    />
  );
};

// Boundary marker component - shows workspace corners
const BoundaryMarker = ({ data }: { data: { corner: string } }) => (
  <div
    className="pointer-events-none flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-[10px] font-bold text-slate-400 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-500"
    title={`Workspace ${data.corner} boundary`}
  >
    {data.corner}
  </div>
);

const nodeTypes = {
  flowStepNode: FlowStepNode,
  boundaryMarker: BoundaryMarker,
  workspaceArea: WorkspaceArea,
};

// ✅ Canvas boundary constants (single source of truth)
// Symmetric workspace centered around origin (0,0) where new steps are created
const CANVAS_BOUNDS = {
  MAX_X: 1600, // ~4 steps to the right
  MAX_Y: 1100, // ~5 steps down
  MIN_X: -800, // ~2 steps to the left (for backward flows)
  MIN_Y: -660, // ~3 steps up (for alternative paths)
} as const;

/**
 * Type for node data containing a FlowStep
 */
interface FlowStepNodeData {
  step: FlowStep;
  onSelect: () => void;
  onEdit: () => void;
  onTransformToSplit?: () => void;
  onDelete?: () => void;
  onPreview?: () => void;
  onGetCheckoutLink?: () => void;
  integrationUrls?: Record<string, string>;
  stepAnalytics?: {
    visitors: number;
    conversions: number;
    progressionRate: number; // % who progressed to next step
    progressedToNext: number; // Count who progressed to next step
    realtimeVisitors: number;
  };
  variantAnalytics?: Record<
    string,
    {
      visitors: number;
      conversions: number;
      progressionRate: number; // % who progressed to next step
      progressedToNext: number; // Count who progressed to next step
      realtimeVisitors: number;
    }
  >;
}

// Known condition labels — function that takes t for i18n
function getConditionLabels(t: (key: string) => string): Record<string, string> {
  return {
    'payment.success': t('flowCanvas.conditionPaid'),
    'payment.failed': t('flowCanvas.conditionFailed'),
    'offer.accepted': t('flowCanvas.conditionAccepted'),
    'offer.declined': t('flowCanvas.conditionDeclined'),
    'mainOrder.exists': t('flowCanvas.conditionHasOrder'),
    'mainOrder.hasProduct': t('flowCanvas.conditionHasProduct'),
    'mainOrder.totalGreaterThan': t('flowCanvas.conditionOrderTotal'),
    'lastOrder.exists': t('flowCanvas.conditionHasOrder'),
    'lastOrder.hasProduct': t('flowCanvas.conditionHasProduct'),
    'lastOrder.totalGreaterThan': t('flowCanvas.conditionOrderTotal'),
    'customer.hasTag': t('flowCanvas.conditionHasTag'),
    'customer.hasAnyTag': t('flowCanvas.conditionHasTags'),
    'customer.hasAllTags': t('flowCanvas.conditionHasAllTags'),
    always: t('flowCanvas.conditionAlways'),
  };
}

function getConditionIcon(key: string): string {
  if (key.startsWith('payment.')) return '💳';
  if (key.startsWith('mainOrder.') || key.startsWith('lastOrder.')) return '🛒';
  if (key.startsWith('offer.')) return '🎁';
  if (key.startsWith('customer.')) return '👤';
  if (key === 'always') return '➡️';
  return '🏷️';
}

function getConditionLabelString(condition: any, t: (key: string) => string): string {
  const conditionLabels = getConditionLabels(t);
  let key = '';
  let label = t('flowCanvas.conditionCond');
  let extra = '';

  if (typeof condition === 'string') {
    key = condition;
    label = conditionLabels[condition] || formatConditionKey(condition);
  } else if (typeof condition === 'object' && condition !== null) {
    const keys = Object.keys(condition);
    key = keys.find((k) => k !== 'and' && k !== 'or' && k !== 'operator') || '';
    if (key) {
      label = conditionLabels[key] || formatConditionKey(key);
      // Try to extract a value to display
      const val = condition[key];
      if (typeof val !== 'object') {
        // e.g. true/false or number
        if (val !== true) extra = `: ${val}`;
      } else if (val) {
        // Object params
        // Prioritize common param names
        const paramVal =
          val.tags ||
          val.tag ||
          val.amount ||
          val.value ||
          val.sku ||
          val.productId ||
          (Object.values(val).length > 0 ? Object.values(val)[0] : undefined);

        if (paramVal && (typeof paramVal === 'string' || typeof paramVal === 'number')) {
          extra = `: ${paramVal}`;
        }
      }
    }
  }

  const icon = getConditionIcon(key);
  return `${icon} ${label}${extra}`;
}

/**
 * Format condition for edge label - clean, minimal labels with emoji icons
 */
function formatConditionForEdge(when: ConditionValue, t: (key: string) => string): string {
  if (typeof when === 'string') {
    return getConditionLabelString(when, t);
  }

  if (typeof when === 'object' && when !== null) {
    // Legacy operator format
    if ('operator' in when) {
      const op = when.operator;
      if (typeof op === 'string') {
        return getConditionLabelString(op, t);
      }
      return t('flowCanvas.condition');
    }

    // JSONLogic compound conditions
    const keys = Object.keys(when);

    if (keys.includes('and')) {
      const conditions = (when as any).and;
      if (Array.isArray(conditions) && conditions.length > 0) {
        const labels = conditions.slice(0, 2).map((c: any) => getConditionLabelString(c, t));
        let text = labels.join(' & ');
        if (conditions.length > 2) text += ` +${conditions.length - 2}`;
        return text;
      }
      return t('flowCanvas.conditionAnd');
    }

    if (keys.includes('or')) {
      const conditions = (when as any).or;
      if (Array.isArray(conditions) && conditions.length > 0) {
        const labels = conditions.slice(0, 2).map((c: any) => getConditionLabelString(c, t));
        let text = labels.join(' | ');
        if (conditions.length > 2) text += ` +${conditions.length - 2}`;
        return text;
      }
      return t('flowCanvas.conditionOr');
    }

    // Single condition object: { "mainOrder.hasProduct": { ... } }
    if (keys.length === 1) {
      // Pass full object to preserve params
      return getConditionLabelString(when, t);
    }
  }

  return t('flowCanvas.condition');
}

/**
 * Format a condition key into readable label
 * e.g., "mainOrder.hasProduct" → "Has Product"
 */
function formatConditionKey(key: string): string {
  // Get the last part after the dot
  const lastPart = key.split('.').pop() || key;
  // Convert camelCase to Title Case with spaces
  return lastPart
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * React Flow-based canvas for advanced funnel visualization
 * Supports A/B testing variants, conditions, priorities
 * ✨ Quick Create: Drag connection to empty space to create new step
 *
 * 🎯 V2 Model: Supports both nodes+edges and legacy steps format
 */
export function FunnelFlowCanvas({
  nodes: propNodes,
  edges: propEdges,
  onUpdateNodes,
  onUpdateEdges,
  steps: propSteps,
  onUpdateSteps,
  selectedStepId,
  selectedEdgeId,
  onSelectStep,
  onEditStep,
  onEditConfig,
  onEditEdge,
  onPreviewStep,
  onRetryScreenshot,
  onGetCheckoutLink,
  onTransformToSplit,
  onDuplicateDesign,
  onDeleteStep,
  onStepCreated,
  onStepLabelChange,
  stepAnalytics,
  variantAnalytics,
  readOnly = false,
  showPreviews = true,
  isPollingInitialScreenshots = false,
  waitingForScreenshotStepId = null,
  storeType,
  connectedProviders = [],
  stepsMissingDesign = [],
  integrationUrls,
  instanceScreenshots,
}: FunnelFlowCanvasProps) {
  const { t } = useTranslation('storefront');
  const { screenToFlowPosition, fitView, getNode, setViewport } = useReactFlow();

  // Stabilize callback refs to prevent infinite re-render loops in initialNodes useMemo.
  // Inline arrow functions from the parent create new references every render, which would
  // invalidate the useMemo and trigger setNodes -> re-render -> new callbacks -> loop.
  const onSelectStepRef = useRef(onSelectStep);
  const onEditStepRef = useRef(onEditStep);
  const onEditConfigRef = useRef(onEditConfig);
  const onPreviewStepRef = useRef(onPreviewStep);
  const onRetryScreenshotRef = useRef(onRetryScreenshot);
  const onGetCheckoutLinkRef = useRef(onGetCheckoutLink);
  const onTransformToSplitRef = useRef(onTransformToSplit);
  const onDuplicateDesignRef = useRef(onDuplicateDesign);
  const onDeleteStepRef = useRef(onDeleteStep);
  const onStepLabelChangeRef = useRef(onStepLabelChange);
  onSelectStepRef.current = onSelectStep;
  onEditStepRef.current = onEditStep;
  onEditConfigRef.current = onEditConfig;
  onPreviewStepRef.current = onPreviewStep;
  onRetryScreenshotRef.current = onRetryScreenshot;
  onGetCheckoutLinkRef.current = onGetCheckoutLink;
  onTransformToSplitRef.current = onTransformToSplit;
  onDuplicateDesignRef.current = onDuplicateDesign;
  onDeleteStepRef.current = onDeleteStep;
  onStepLabelChangeRef.current = onStepLabelChange;

  // State to force show action bar on first step (for guided tour)
  const [forceShowFirstStepActionBar, setForceShowFirstStepActionBar] = React.useState(false);

  React.useEffect(() => {
    const handleResetView = (e: Event) => {
      const animated = (e as CustomEvent).detail?.animated;
      fitView({
        padding: 0.3,
        duration: animated ? 400 : 0,
        maxZoom: 1,
        minZoom: 0.5,
      });
    };

    window.addEventListener('funnel-editor-reset-view', handleResetView);
    return () => window.removeEventListener('funnel-editor-reset-view', handleResetView);
  }, [fitView]);

  // 🎯 Listen for show preview button event (used by guided tour)
  React.useEffect(() => {
    const handleShowPreviewButton = () => {
      setForceShowFirstStepActionBar(true);
    };

    const handleHidePreviewButton = () => {
      setForceShowFirstStepActionBar(false);
    };

    window.addEventListener('funnel-editor-show-preview-button', handleShowPreviewButton);
    window.addEventListener('funnel-editor-hide-preview-button', handleHidePreviewButton);
    return () => {
      window.removeEventListener('funnel-editor-show-preview-button', handleShowPreviewButton);
      window.removeEventListener('funnel-editor-hide-preview-button', handleHidePreviewButton);
    };
  }, []);

  // 🎯 Notify tour library when viewport changes (zoom/pan) so highlights can reposition
  const triggerTourReposition = React.useCallback(() => {
    // Dispatch a resize event to trigger tour libraries to recalculate positions
    // This is a common pattern since most tour libraries listen for resize
    window.dispatchEvent(new Event('resize'));
  }, []);

  // Throttle viewport change updates during continuous movement.
  // 200ms, not per-frame: the synthetic resize event wakes EVERY resize
  // listener in the app (React Flow, charts, resizable panels) — at 16ms it
  // ran 60×/sec during canvas pan/zoom for tour repositioning alone (FE-08).
  // onMoveEnd still fires a final reposition, so tours settle correctly.
  const lastMoveUpdate = React.useRef(0);
  const handleViewportMove = React.useCallback(() => {
    const now = Date.now();
    if (now - lastMoveUpdate.current > 200) {
      lastMoveUpdate.current = now;
      triggerTourReposition();
    }
  }, [triggerTourReposition]);

  // Fresh funnelNodes for the auto-center timeout without adding it to the
  // effect deps (which would re-center the viewport on every node change).
  // (funnelNodes is declared later via useMemo; the ref is written by an
  // effect below it and only READ inside the auto-center timeout, which
  // always fires after mount.)
  const funnelNodesRef = React.useRef<FunnelNode[]>([]);

  // 🎯 Auto-center on selected step/edge (or show all if none selected)
  React.useEffect(() => {
    let cancelled = false;
    const targetStepId = selectedStepId; // Capture current value
    const targetEdgeId = selectedEdgeId; // Capture edge selection

    // If edge selected, center on the edge (midpoint between source and target)
    if (targetEdgeId && !targetStepId) {
      const edge = propEdges?.find((e) => e.id === targetEdgeId);
      if (edge) {
        const sourceNode = getNode(edge.source);
        const targetNode = getNode(edge.target);

        if (sourceNode && targetNode) {
          requestAnimationFrame(() => {
            if (!cancelled) {
              // Calculate midpoint between source and target
              const midX = (sourceNode.position.x + targetNode.position.x) / 2 + 150; // +150 to center on node width (300px)
              const midY = (sourceNode.position.y + targetNode.position.y) / 2;

              fitView({
                nodes: [sourceNode, targetNode], // Show both nodes connected by the edge
                duration: 300,
                padding: 0.3,
                maxZoom: 1.2,
                minZoom: 0.5,
              });
            }
          });

          return () => {
            cancelled = true;
          };
        }
      }
    }

    // If no step/edge selected, show all real steps (excluding workspace markers)
    // Delay to allow sidebar panel to close and viewport to resize
    if (!targetStepId && !targetEdgeId) {
      const timeoutId = setTimeout(() => {
        if (!cancelled) {
          // Get only real nodes (not workspace area or boundary markers).
          // Read through the ref: funnelNodes isn't in this effect's deps
          // (re-centering on every node change would thrash the viewport),
          // so the closure captured a stale list on deselect-fit (FE-18).
          const realNodes = funnelNodesRef.current.filter((n) => !n.id.startsWith('__'));

          if (realNodes.length === 0) {
            // No real steps yet - center on origin (where new steps will be created)
            fitView({
              duration: 300,
              padding: 0.5,
              maxZoom: 0.8,
              minZoom: 0.5,
            });
          } else {
            fitView({
              duration: 300, // Smooth animation
              padding: 0.35, // Add padding around nodes
              maxZoom: 0.9, // Don't zoom in too much for overview
              minZoom: 0.5, // Keep zoom reasonable
            });
          }
        }
      }, 150); // 150ms delay to allow sidebar panel to close

      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    }

    // If step selected, center on that specific step
    const attemptCenter = (attempts = 0) => {
      if (cancelled) {
        return;
      }

      if (!targetStepId) return; // Type guard

      const node = getNode(targetStepId);
      if (node) {
        // Use requestAnimationFrame to ensure React Flow has finished rendering
        requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }

          fitView({
            nodes: [node],
            duration: 300, // Smooth animation
            padding: 0.3, // Add padding around the node (30% of viewport)
            maxZoom: 1.2, // Don't zoom in too much
            minZoom: 0.5, // Don't zoom out too much
          });
        });
      } else {
        if (attempts < 5) {
          // Retry up to 5 times with increasing delays (for newly created nodes)
          setTimeout(() => attemptCenter(attempts + 1), 50 * (attempts + 1));
        }
      }
    };

    // Start with slightly larger delay to ensure React Flow is ready
    const timeoutId = setTimeout(() => attemptCenter(), 100);

    // Cleanup: cancel if selection changes before centering completes
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedStepId, selectedEdgeId, propEdges, fitView, getNode]);

  // 🔄 Normalize input: Convert steps to nodes+edges if needed
  const { funnelNodes, funnelEdges } = useMemo(() => {
    if (propNodes && propNodes.length > 0 && propEdges) {
      return { funnelNodes: propNodes, funnelEdges: propEdges };
    }

    if (propSteps && Array.isArray(propSteps) && propSteps.length > 0) {
      const allNodes: FunnelNode[] = [];
      const allEdges: FunnelEdge[] = [];

      for (const step of propSteps) {
        const { node, edges: stepEdges } = flowStepToNode(step);
        allNodes.push(node);
        allEdges.push(...stepEdges);
      }

      return { funnelNodes: allNodes, funnelEdges: allEdges };
    }

    return { funnelNodes: [], funnelEdges: [] };
  }, [propNodes, propEdges, propSteps]);

  // Keep the auto-center ref (declared above the effect) in sync.
  funnelNodesRef.current = funnelNodes;

  // ⌨️ Keyboard navigation: Arrow keys to jump between connected steps, Escape to deselect
  React.useEffect(() => {
    if (readOnly) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Escape: Deselect current step/edge and close editors
      if (e.key === 'Escape') {
        if (selectedStepId) {
          onSelectStep(null);
          e.preventDefault();
        } else if (selectedEdgeId && onEditEdge) {
          onEditEdge(null);
          e.preventDefault();
        }
        return;
      }

      // If no step selected and arrow key pressed, select first step
      if (!selectedStepId) {
        const isArrowKey = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key);
        if (isArrowKey && funnelNodes.length > 0) {
          onSelectStep(funnelNodes[0].id);
          e.preventDefault();
        }
        return;
      }

      // Arrow navigation when a step is selected
      let nextStepId: string | null = null;

      switch (e.key) {
        case 'ArrowRight': {
          // Go to next step (follow outgoing edges)
          const outgoingEdges = funnelEdges.filter((edge) => edge.source === selectedStepId);
          if (outgoingEdges.length > 0) {
            nextStepId = outgoingEdges[0].target;
            e.preventDefault();
          }
          break;
        }
        case 'ArrowLeft': {
          // Go to previous step (follow incoming edges)
          const incomingEdges = funnelEdges.filter((edge) => edge.target === selectedStepId);
          if (incomingEdges.length > 0) {
            nextStepId = incomingEdges[0].source;
            e.preventDefault();
          }
          break;
        }
        case 'ArrowDown': {
          // Go to next step in list (by index)
          const currentIndex = funnelNodes.findIndex((node) => node.id === selectedStepId);
          if (currentIndex >= 0 && currentIndex < funnelNodes.length - 1) {
            nextStepId = funnelNodes[currentIndex + 1].id;
            e.preventDefault();
          }
          break;
        }
        case 'ArrowUp': {
          // Go to previous step in list (by index)
          const currentIndex = funnelNodes.findIndex((node) => node.id === selectedStepId);
          if (currentIndex > 0) {
            nextStepId = funnelNodes[currentIndex - 1].id;
            e.preventDefault();
          }
          break;
        }
      }

      if (nextStepId) {
        onSelectStep(nextStepId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedStepId, selectedEdgeId, funnelNodes, funnelEdges, onSelectStep, onEditEdge, readOnly]);

  // Quick create modal state
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const quickCreateData = useRef<{
    sourceStepId: string;
    position: { x: number; y: number };
    handleId?: string;
  } | null>(null);

  const [quickConditionEdge, setQuickConditionEdge] = useState<{
    edgeId: string;
    position: { x: number; y: number };
    sourceType: string;
    /** Condition already applied to the edge at creation (e.g. checkout default) */
    appliedKey?: string;
  } | null>(null);

  // Track newly created node for auto-edit
  const [newlyCreatedNodeId, setNewlyCreatedNodeId] = useState<string | null>(null);

  // Track connection source (nodeId + handleId for directional node creation)
  const connectionSourceRef = useRef<{ nodeId: string; handleId: string } | null>(null);

  // Track if a successful connection was made (to avoid showing quick create)
  const connectionMadeRef = useRef<boolean>(false);
  // Per-node React Flow node cache (FE-09): the rebuild below used to create
  // fresh data objects (with fresh closures) for EVERY node whenever any
  // input changed — selection, one label edit or a screenshot arriving
  // re-rendered all N FlowStepNodes (React Flow memoizes on data identity).
  // Each node's RF object is now reused unless its own value-fingerprint
  // changes; the fingerprint mirrors exactly the field set the global
  // nodeFingerprint (the setNodes gate) already tracks, so cache blindness
  // is identical to the existing resync blindness — no new staleness class.
  const nodeCacheRef = useRef(new Map<string, { fp: string; rfNode: Node }>());
  // Identity-tracked globals that feed node data but have no cheap value
  // hash — bump a version when any identity changes.
  const dataGlobalsRef = useRef<{ integrationUrls: unknown; connectedProviders: unknown; version: number }>({
    integrationUrls: undefined, connectedProviders: undefined, version: 0,
  });
  if (
    dataGlobalsRef.current.integrationUrls !== integrationUrls ||
    dataGlobalsRef.current.connectedProviders !== connectedProviders
  ) {
    dataGlobalsRef.current = { integrationUrls, connectedProviders, version: dataGlobalsRef.current.version + 1 };
  }

  // Convert FunnelNode[] to React Flow nodes
  const initialNodes: Node[] = useMemo(() => {
    const { MAX_X, MAX_Y, MIN_X, MIN_Y } = CANVAS_BOUNDS;

    // Workspace area background - shows the accessible zone
    const workspaceAreaNode: Node = {
      id: '__workspace_area',
      type: 'workspaceArea',
      position: { x: MIN_X, y: MIN_Y },
      data: {},
      draggable: false,
      selectable: false,
      deletable: false,
      zIndex: -1000, // Render behind everything
    };

    // Create boundary marker nodes to show workspace limits
    const boundaryMarkers: Node[] = [
      {
        id: '__boundary_top_left',
        type: 'boundaryMarker',
        position: { x: MIN_X - 10, y: MIN_Y - 10 },
        data: { corner: '↖' },
        draggable: false,
        selectable: false,
        deletable: false,
      },
      {
        id: '__boundary_top_right',
        type: 'boundaryMarker',
        position: { x: MAX_X + 385, y: MIN_Y - 10 },
        data: { corner: '↗' },
        draggable: false,
        selectable: false,
        deletable: false,
      },
      {
        id: '__boundary_bottom_left',
        type: 'boundaryMarker',
        position: { x: MIN_X - 10, y: MAX_Y + 195 },
        data: { corner: '↙' },
        draggable: false,
        selectable: false,
        deletable: false,
      },
      {
        id: '__boundary_bottom_right',
        type: 'boundaryMarker',
        position: { x: MAX_X + 385, y: MAX_Y + 195 },
        data: { corner: '↘' },
        draggable: false,
        selectable: false,
        deletable: false,
      },
    ];

    // Find the first configurable step for guided tour targeting
    // Skip Shopify storefront steps as they have a different editor without design selection
    const firstConfigurableStepId = (() => {
      // First try entry step that's not Shopify
      const entryStep = funnelNodes.find((n) => n.isDefault && n.type !== 'storefront_shopify');
      if (entryStep) return entryStep.id;
      
      // If entry is Shopify, find the first checkout/offer step
      const configurableStep = funnelNodes.find((n) => 
        n.type !== 'storefront_shopify' && 
        n.type !== 'thankyou' && 
        n.kind !== 'condition'
      );
      return configurableStep?.id || funnelNodes[0]?.id;
    })();

    // Shared inputs for every node's fingerprint (value-hashed or versioned).
    const analyticsHash = (a?: { visitors: number; conversions: number; realtimeVisitors: number; progressedToNext: number; conversionValue: number }) =>
      a ? `${a.visitors}/${a.conversions}/${a.realtimeVisitors}/${a.progressedToNext}/${a.conversionValue}` : '';
    const vanHash = variantAnalytics
      ? Object.entries(variantAnalytics).map(([k, a]) => `${k}=${analyticsHash(a)}`).join(',')
      : '';
    const screenshotsHash = instanceScreenshots
      ? Object.entries(instanceScreenshots).map(([k, v]) => `${k}=${v}`).join(',')
      : '';
    const globalFp = `${showPreviews}|${readOnly}|${forceShowFirstStepActionBar}|${vanHash}|${screenshotsHash}|g${dataGlobalsRef.current.version}`;

    // Incoming edges indexed once (was an O(E) filter per node).
    const incomingByTarget = new Map<string, typeof funnelEdges>();
    for (const e of funnelEdges) {
      const list = incomingByTarget.get(e.target);
      if (list) list.push(e); else incomingByTarget.set(e.target, [e]);
    }

    const seenIds = new Set<string>();
    const stepNodes = funnelNodes.map((node, index) => {
      seenIds.add(node.id);
      let position;

      if (node.position) {
        // ✅ Validate and constrain existing position
        const x = node.position.x;
        const y = node.position.y;

        if (x < MIN_X || x > MAX_X || y < MIN_Y || y > MAX_Y) {
          position = {
            x: Math.max(MIN_X, Math.min(MAX_X, x)),
            y: Math.max(MIN_Y, Math.min(MAX_Y, y)),
          };
        } else {
          position = node.position;
        }
      } else {
        // Fallback: calculate from index/row
        const STEP_WIDTH = 400;
        const STEP_HEIGHT = 220;
        const MARGIN = 80;
        const GRID_X = STEP_WIDTH + MARGIN;
        const GRID_Y = STEP_HEIGHT + MARGIN;

        const calculatedX = Math.min((node.index ?? index) * GRID_X, MAX_X);
        const calculatedY = Math.min((node.row ?? 0) * GRID_Y, MAX_Y);

        position = { x: calculatedX, y: calculatedY };
      }

      const incomingEdges = incomingByTarget.get(node.id) ?? [];

      // Check if this step is missing a design (and which variants specifically)
      const missingForNode = stepsMissingDesign.filter((s) => s.id === node.id);
      const isMissingDesign = missingForNode.length > 0;
      const missingDesignVariantIds = new Set(missingForNode.map(s => s.variantId).filter(Boolean));

      // Per-node value fingerprint (FE-09) — same field set as the global
      // nodeFingerprint, plus this node's slice of the per-node globals.
      const isSelected = node.id === selectedStepId;
      const isWaiting = waitingForScreenshotStepId === node.id
        || (ROUTABLE_CAPTURE_TYPES.has(node.type)
            && !isMissingDesign
            && !nodeHasUsablePreview(node, instanceScreenshots)
            && !nodeHasCaptureError(node));
      const variantPart = node.variants?.map(v => `${v.id}:${v.config?.instanceId || ''}:${v.config?.variant || ''}:${(node as any).metadata?.variantScreenshots?.[v.id]?.url || ''}`).join(';') || '';
      const edgesSig = incomingEdges.map(e => `${e.id}:${e.source}:${(e as any).label || ''}`).join(';');
      const nodeFp = `${globalFp}|${position.x},${position.y}|${node.config?.instanceId || ''}|${node.config?.variant || ''}|${node.name || ''}|${(node as any).label || ''}|${variantPart}|${(node.metadata as any)?.previewScreenshot?.url || ''}|ds:${(node.metadata as any)?.designSavedAt || ''}|pe:${(node.metadata as any)?.previewScreenshotError?.failedAt || ''}|${edgesSig}|sel:${isSelected}|ae:${node.id === newlyCreatedNodeId}|an:${analyticsHash(stepAnalytics?.[node.id])}|w:${isWaiting}|f:${node.id === firstConfigurableStepId}|md:${isMissingDesign}:${[...missingDesignVariantIds].join(',')}`;

      const cached = nodeCacheRef.current.get(node.id);
      if (cached && cached.fp === nodeFp) {
        return cached.rfNode;
      }

      // Convert node to FlowStep for components that expect it
      const step = nodeToFlowStep(node, incomingEdges);

      const rfNode: Node = {
        id: node.id,
        type: 'flowStepNode',
        position,
        data: {
          step,
          node,
          isSelected,
          onSelect: () => onSelectStepRef.current(node.id),
          onEdit: onEditStepRef.current ? () => onEditStepRef.current!(node.id, 'design') : undefined,
          onEditConfig: onEditConfigRef.current ? () => onEditConfigRef.current!(node.id) : undefined,
          onPreview: onPreviewStepRef.current ? () => onPreviewStepRef.current!(node.id) : undefined,
          // No isDefault condition: that flag marks where a session starts when nothing names a step,
          // not where cold traffic may land. Any mounted checkout can be linked.
          onGetCheckoutLink: (
            onGetCheckoutLinkRef.current &&
            step.type === 'checkout' &&
            (step.config?.instanceId || step.variants?.[0]?.config?.instanceId)
          ) ? () => onGetCheckoutLinkRef.current!(node.id) : undefined,
          onTransformToSplit: onTransformToSplitRef.current ? () => onTransformToSplitRef.current!(node.id) : undefined,
          onDuplicateDesign: onDuplicateDesignRef.current ? () => onDuplicateDesignRef.current!(node.id) : undefined,
          onDelete: onDeleteStepRef.current ? () => onDeleteStepRef.current!(node.id) : undefined,
          onLabelChange: onStepLabelChangeRef.current
            ? (newLabel: string) => onStepLabelChangeRef.current!(node.id, newLabel)
            : undefined,
          autoEdit: node.id === newlyCreatedNodeId,
          stepAnalytics: stepAnalytics?.[node.id],
          variantAnalytics: variantAnalytics,
          showPreviews,
          // Show the full-height "Capturing…" spinner on a routable step block
          // whenever a preview is pending — so the block keeps its size and
          // reads as "working" instead of collapsing to a bare header. Shown
          // when, for a routable step:
          //   - it has a design (not the "Choose Design" empty state), AND
          //   - it has no usable preview yet (no capture + no deployment
          //     thumbnail fallback), AND
          //   - the capture hasn't errored.
          // The two terminal exits keep this bounded: a successful capture
          // makes `nodeHasUsablePreview` true → the rendered thumbnail shows;
          // a failed capture sets an error marker → `nodeHasCaptureError` true
          // → the block's error/retry state shows. Either way the spinner
          // clears. The explicit `waitingForScreenshotStepId` covers the
          // Preview-button path. (Polling still drives the editor's refetch so
          // the resolved state lands without a manual reload — see
          // [funnelId].tsx — but the spinner itself no longer depends on it,
          // which is why the block stopped shrinking when polling stopped.)
          isWaitingForScreenshot: isWaiting,
          onRetryScreenshot: onRetryScreenshotRef.current
            ? (variantId?: string) => onRetryScreenshotRef.current!(node.id, variantId)
            : undefined,
          isFirstStep: node.id === firstConfigurableStepId,
          forceShowActionBar: node.id === firstConfigurableStepId && forceShowFirstStepActionBar,
          isMissingDesign,
          missingDesignVariantIds,
          integrationUrls,
          instanceScreenshots,
          connectedProviders,
        },
        selected: isSelected,
        deletable: !readOnly,
      };

      nodeCacheRef.current.set(node.id, { fp: nodeFp, rfNode });
      return rfNode;
    });

    // Evict cache entries for deleted nodes.
    for (const id of nodeCacheRef.current.keys()) {
      if (!seenIds.has(id)) nodeCacheRef.current.delete(id);
    }

    // Combine workspace area, boundary markers and step nodes
    return [workspaceAreaNode, ...boundaryMarkers, ...stepNodes];
  }, [
    funnelNodes,
    funnelEdges,
    selectedStepId,
    newlyCreatedNodeId,
    stepAnalytics,
    variantAnalytics,
    showPreviews,
    waitingForScreenshotStepId,
    isPollingInitialScreenshots,
    readOnly,
    forceShowFirstStepActionBar,
    stepsMissingDesign,
    integrationUrls,
    instanceScreenshots,
    connectedProviders,
  ]);

  // Convert FunnelEdge[] to React Flow edges
  const initialEdges: Edge[] = useMemo(() => {
    const reactFlowEdges: Edge[] = [];

    funnelEdges.forEach((edge) => {
      const sourceNode = funnelNodes.find((n) => n.id === edge.source);
      const targetNode = funnelNodes.find((n) => n.id === edge.target);

      if (!sourceNode || !targetNode) {
        return;
      }

      // Determine edge style based on condition
      let edgeLabel = edge.label || '';
      let strokeColor = '#cbd5e1'; // Default: slate-300
      const animated = false;
      let isDashed = false;

      // Entry edge (to entry node)
      if (targetNode.isDefault && !sourceNode) {
        edgeLabel = edgeLabel || t('flowCanvas.edgeStart');
        strokeColor = '#86efac'; // green-300
      }
      // Conditional edge
      else if (edge.conditions?.when) {
        edgeLabel = edgeLabel || formatConditionForEdge(edge.conditions.when, t);
        strokeColor = '#c4b5fd'; // violet-300
      }
      // Bypass edge
      else if (edge.conditions?.bypassTo) {
        edgeLabel = edgeLabel || t('flowCanvas.edgeFallback');
        strokeColor = '#fca5a5'; // red-300
        isDashed = true;
      }
      // Default path (no condition) — keep label empty for clean look
      else {
        edgeLabel = edgeLabel || '';
        strokeColor = '#cbd5e1'; // slate-300
      }

      // Add priority as small suffix
      if (edge.conditions?.priority !== undefined) {
        edgeLabel = edgeLabel
          ? `${edgeLabel} · P${edge.conditions.priority}`
          : `P${edge.conditions.priority}`;
      }

      reactFlowEdges.push({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        // Use stored handles or default to right → left (forward flow)
        sourceHandle: edge.sourceHandle || HANDLE_IDS.RIGHT,
        targetHandle: edge.targetHandle || HANDLE_IDS.LEFT,
        label: edgeLabel || undefined,
        animated,
        style: {
          stroke: strokeColor,
          strokeWidth: 1.5,
          ...(isDashed ? { strokeDasharray: '4,4' } : {}),
        },
        type: 'smoothstep',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: strokeColor,
          width: 16,
          height: 16,
        },
        labelStyle: {
          fontSize: 11,
          fontWeight: 600,
          fill: isDashed ? '#ef4444' : '#475569',
          cursor: 'pointer',
        },
        labelBgStyle: {
          fill: '#ffffff',
          fillOpacity: 0.95,
          cursor: 'pointer',
          stroke: '#e2e8f0',
          strokeWidth: 1,
        },
        labelBgPadding: [8, 4] as [number, number],
        labelBgBorderRadius: 4,
        deletable: !readOnly,
      });
    });

    return reactFlowEdges;
  }, [funnelEdges, funnelNodes, t, readOnly]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Track previous nodes length to detect additions
  const prevNodesLength = useRef(nodes.length);

  // Auto-center when nodes are added (including initial load)
  React.useEffect(() => {
    // Filter out workspace/boundary nodes to count only real steps
    const realNodes = nodes.filter((n) => !n.id.startsWith('__'));
    const prevRealCount = prevNodesLength.current;

    // If real nodes count increased, fit view to show all content
    if (realNodes.length > prevRealCount) {
      // Small delay to allow render
      setTimeout(() => {
        fitView({
          padding: 0.4,
          duration: 600,
          maxZoom: 0.9,
          nodes: realNodes, // Only fit to real nodes, not boundary markers
        });
      }, 50);
    }
    prevNodesLength.current = realNodes.length;
  }, [nodes, fitView]);

  // Sync React Flow internal state when the logical node/edge data changes.
  // We derive a lightweight "fingerprint" from the structural data so that
  // cosmetic re-renders (new object identities, new callback refs) don't
  // trigger an infinite setNodes → re-render → setNodes loop. Analytics are
  // included by VALUE (see analyticsValueHash below), not identity.
  const nodeFingerprint = useMemo(() => {
    const nodesPart = funnelNodes.map(n => {
      const variantPart = n.variants?.map(v => `${v.config?.instanceId || ''}:${v.config?.variant || ''}`).join(';') || '';
      // Include the per-node preview URL so when a capture arrives via
      // polling the fingerprint changes, setNodes fires, and the
      // "Capturing..." spinner switches to the rendered screenshot.
      const previewUrl = (n.metadata as any)?.previewScreenshot?.url || '';
      // designSavedAt + error marker drive the "updating preview" overlay on
      // step nodes — without them in the fingerprint the overlay would only
      // appear/clear when an unrelated structural change resyncs the canvas.
      const designSavedAt = (n.metadata as any)?.designSavedAt || '';
      const previewErrAt = (n.metadata as any)?.previewScreenshotError?.failedAt || '';
      return `${n.id}:${n.position?.x}:${n.position?.y}:${n.config?.instanceId || ''}:${n.config?.variant || ''}:${n.name || ''}:${variantPart}:pv=${previewUrl}:ds=${designSavedAt}:pe=${previewErrAt}`;
    }).join('|');
    const screenshotHash = instanceScreenshots
      ? Object.entries(instanceScreenshots).map(([k, v]) => `${k}=${v}`).join(',')
      : '';
    const missingDesignIds = stepsMissingDesign.map(s => s.id).join(',');
    // Coarse VALUE hash of the analytics (not object identity, which changes
    // every poll): without it the 15s analytics poll paid its cost but node
    // badges (visitors / realtime / progression) stayed frozen until an
    // unrelated structural change re-synced the canvas (audit FE-01). The
    // fingerprint only changes when the numbers actually change.
    const analyticsValueHash = (rec?: Record<string, { visitors: number; conversions: number; realtimeVisitors: number; progressedToNext: number; conversionValue: number }>) =>
      rec
        ? Object.entries(rec)
            .map(([k, a]) => `${k}=${a.visitors}/${a.conversions}/${a.realtimeVisitors}/${a.progressedToNext}/${a.conversionValue}`)
            .join(',')
        : '';
    return nodesPart
      + `|sel:${selectedStepId}|ro:${readOnly}|prev:${showPreviews}|wait:${waitingForScreenshotStepId}|poll:${isPollingInitialScreenshots ? 1 : 0}`
      + `|new:${newlyCreatedNodeId}|tour:${forceShowFirstStepActionBar}|ss:${screenshotHash}`
      + `|missing:${missingDesignIds}`
      + `|an:${analyticsValueHash(stepAnalytics)}|van:${analyticsValueHash(variantAnalytics)}`;
  }, [funnelNodes, selectedStepId, readOnly, showPreviews, waitingForScreenshotStepId, isPollingInitialScreenshots, newlyCreatedNodeId, forceShowFirstStepActionBar, instanceScreenshots, stepsMissingDesign, stepAnalytics, variantAnalytics]);

  React.useEffect(() => {
    setNodes(initialNodes);
  }, [nodeFingerprint, setNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Handle connection between nodes (drag & drop)
  const onConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return;

      if (params.source && params.target) {
        connectionMadeRef.current = true;

        // Look up the FUNNEL node type (checkout/offer/landing…) — the
        // React Flow node's type is always 'flowStepNode', which has no
        // entry in QUICK_CONDITIONS_BY_SOURCE, so the contextual presets
        // (Payment Success/Failed, Accepted/Declined…) never showed (FE-07).
        const srcFunnelNode = funnelNodes.find((n) => n.id === params.source);
        const defaultConditions = defaultEdgeConditions(srcFunnelNode?.type);

        const newEdge = createEdge(
          params.source,
          params.target,
          params.sourceHandle || undefined,
          params.targetHandle || undefined,
          defaultConditions,
        );

        if (onUpdateEdges) {
          const updatedEdges = [...funnelEdges, newEdge];
          onUpdateEdges(updatedEdges);

          const sourceNode = getNode(params.source);
          const targetNode = getNode(params.target);
          if (sourceNode && targetNode) {
            const midX = (sourceNode.position.x + targetNode.position.x) / 2 + 150;
            const midY = (sourceNode.position.y + targetNode.position.y) / 2;
            setQuickConditionEdge({
              edgeId: newEdge.id,
              position: { x: midX, y: midY },
              sourceType: srcFunnelNode?.type || 'custom',
              appliedKey: defaultConditions?.when as string | undefined,
            });
          } else if (onEditEdge) {
            setTimeout(() => onEditEdge(newEdge.id), 50);
          }
        } else if (onUpdateSteps && propSteps) {
          const updatedSteps = propSteps.map((step) => {
            if (step.id === params.target) {
              const from = step.conditions.from || [];
              if (!from.includes(params.source!)) {
                return { ...step, conditions: { ...step.conditions, from: [...from, params.source!] } };
              }
            }
            return step;
          });
          onUpdateSteps(updatedSteps);
        }
      }
    },
    [funnelEdges, funnelNodes, readOnly, onUpdateEdges, onUpdateSteps, propSteps, onEditEdge, getNode],
  );

  // Handle node deletion with automatic edge reconnection
  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      if (readOnly) return;

      // Filter out workspace area and boundary markers - they can't be deleted
      const deletableNodes = deleted.filter((n) => !n.id.startsWith('__'));
      if (deletableNodes.length === 0) return;

      const deletedNodeIds = new Set(deletableNodes.map((n) => n.id));

      // Calculate new edges with reconnection logic. Computed from the `edges`
      // state value OUTSIDE the setEdges updater: the previous code read the
      // updater's result synchronously, which relies on React's eager-state
      // optimization — skipped when another update is already queued (node
      // deletion schedules onNodesChange first), leaving the captured array
      // empty and persisting an EMPTY edge list for the whole funnel.
      let remainingNodes = [...nodes];

      const newEdges = deletableNodes.reduce<Edge[]>((acc, node) => {
          // Get incoming nodes (nodes connected as source -> deleted node)
          const incomers = getIncomers(node, remainingNodes, acc);
          // Get outgoing nodes (deleted node -> target nodes)
          const outgoers = getOutgoers(node, remainingNodes, acc);
          // Get all edges connected to this node
          const connectedEdges = getConnectedEdges([node], acc);

          // Remove edges connected to the deleted node
          const remainingEdges = acc.filter((edge) => !connectedEdges.includes(edge));

          // Create new edges connecting incomers to outgoers
          const createdEdges = incomers.flatMap(({ id: source }) =>
            outgoers.map(({ id: target }) => {
              return {
                id: generateEdgeId(source, target),
                source,
                target,
                sourceHandle: HANDLE_IDS.RIGHT, // Default forward flow
                targetHandle: HANDLE_IDS.LEFT, // Default forward flow
                type: 'smoothstep',
                animated: false,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 20,
                  height: 20,
                },
              };
            }),
          );

          // Update remaining nodes list
          remainingNodes = remainingNodes.filter((rn) => rn.id !== node.id);

          return [...remainingEdges, ...createdEdges];
        }, edges);

      setEdges(newEdges);

      // Remove deleted nodes from the funnel state
      if (onUpdateNodes && funnelNodes) {
        const updatedNodes = funnelNodes.filter((node) => !deletedNodeIds.has(node.id));
        onUpdateNodes(updatedNodes);
      }

      // Update edges through parent callback
      if (onUpdateEdges && funnelEdges) {
        // Convert React Flow edges back to FunnelEdges
        const updatedFunnelEdges: FunnelEdge[] = newEdges.map((edge) => {
          const existingEdge = funnelEdges.find((e) => e.id === edge.id);
          const sourceType = funnelNodes.find((n) => n.id === edge.source)?.type;
          return (
            existingEdge || {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              label: edge.label as string | undefined,
              conditions: defaultEdgeConditions(sourceType) ?? {},
            }
          );
        });

        onUpdateEdges(updatedFunnelEdges);
      }
    },
    [nodes, edges, setEdges, funnelNodes, funnelEdges, readOnly, onUpdateNodes, onUpdateEdges],
  );

  // Persist edge deletion (Delete/Backspace on a selected edge). React Flow's
  // built-in delete handling only removes the edge from its local state — the
  // funnel's edges prop stays untouched, so the next setEdges(initialEdges)
  // re-sync (e.g. after a node drag persists positions) resurrects the
  // "deleted" edge. When a NODE is deleted, React Flow fires this first with
  // the connected edges and then onNodesDelete, whose onUpdateEdges call
  // (including auto-reconnection edges) overwrites this intermediate update —
  // so no coordination between the two handlers is needed.
  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (readOnly || deleted.length === 0) return;

      const deletedIds = new Set(deleted.map((e) => e.id));

      if (onUpdateEdges) {
        onUpdateEdges(funnelEdges.filter((e) => !deletedIds.has(e.id)));
      } else if (onUpdateSteps && propSteps) {
        // Legacy steps format: an edge is a `source` entry in the target
        // step's conditions.from
        const removedSourcesByTarget = new Map<string, Set<string>>();
        for (const e of deleted) {
          const set = removedSourcesByTarget.get(e.target) ?? new Set<string>();
          set.add(e.source);
          removedSourcesByTarget.set(e.target, set);
        }
        const updatedSteps = propSteps.map((step) => {
          const removed = removedSourcesByTarget.get(step.id);
          if (!removed || !step.conditions?.from) return step;
          return {
            ...step,
            conditions: {
              ...step.conditions,
              from: step.conditions.from.filter((source) => !removed.has(source)),
            },
          };
        });
        onUpdateSteps(updatedSteps);
      }
    },
    [funnelEdges, readOnly, onUpdateEdges, onUpdateSteps, propSteps],
  );

  // ✅ Handle node drag end - persist positions
  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent | React.TouchEvent, node: Node) => {
      if (readOnly) return;

      const { MAX_X, MAX_Y, MIN_X, MIN_Y } = CANVAS_BOUNDS;

      const constrainedPosition = {
        x: Math.max(MIN_X, Math.min(MAX_X, node.position.x)),
        y: Math.max(MIN_Y, Math.min(MAX_Y, node.position.y)),
      };

      if (onUpdateNodes) {
        // ✅ New: Update nodes directly
        const updatedNodes = funnelNodes.map((n) => {
          if (n.id === node.id) {
            return {
              ...n,
              position: { ...constrainedPosition },
            };
          }
          return n;
        });
        onUpdateNodes(updatedNodes);
      } else if (onUpdateSteps && propSteps) {
        // 🔄 Legacy: Update steps
        const updatedSteps = propSteps.map((step) => {
          if (step.id === node.id) {
            return {
              ...step,
              flowPosition: { ...constrainedPosition },
            };
          }
          return step;
        });
        onUpdateSteps(updatedSteps);
      }
    },
    [funnelNodes, onUpdateNodes, onUpdateSteps, propSteps, readOnly],
  );

  /**
   * Track connection start for quick create feature
   * Stores source node and handle for use in onConnectEnd
   */
  const onConnectStart = useCallback(
    (
      _event: React.MouseEvent | React.TouchEvent,
      params: { nodeId: string | null; handleId: string | null; handleType: 'source' | 'target' | null },
    ) => {
      if (readOnly) return;
      connectionMadeRef.current = false;
      if (params?.nodeId && params?.handleId) {
        connectionSourceRef.current = { nodeId: params.nodeId, handleId: params.handleId };
      }
    },
    [readOnly],
  );

  /**
   * Handle connection end - trigger quick create when dropped on empty canvas
   * 
   * Valid drop targets for quick create:
   * - react-flow__pane (canvas background)
   * - react-flow__renderer (canvas renderer)  
   * - workspaceArea node (visual background element)
   * - boundaryMarker nodes (visual corner markers)
   */
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (readOnly) return;
      const mouseEvent = event as MouseEvent;
      const connectionSource = connectionSourceRef.current;

      // Early exit if no source or connection was already made to a node
      if (!connectionSource) {
        connectionSourceRef.current = null;
        return;
      }

      if (connectionMadeRef.current) {
        connectionSourceRef.current = null;
        connectionMadeRef.current = false;
        return;
      }

      const { nodeId: sourceStepId, handleId } = connectionSource;
      const target = mouseEvent?.target as HTMLElement;

      // Determine if dropped on valid canvas area (not a real step node)
      const isCanvasBackground =
        target?.classList?.contains('react-flow__pane') ||
        target?.classList?.contains('react-flow__renderer');
      
      const isWorkspaceOrBoundary =
        target?.classList?.contains('react-flow__node-workspaceArea') ||
        target?.classList?.contains('react-flow__node-boundaryMarker') ||
        target?.closest('.react-flow__node-workspaceArea') !== null ||
        target?.closest('.react-flow__node-boundaryMarker') !== null;

      const isDroppedOnCanvas = isCanvasBackground || isWorkspaceOrBoundary;

      if (isDroppedOnCanvas && mouseEvent?.clientX && mouseEvent?.clientY) {
        let position = screenToFlowPosition({
          x: mouseEvent.clientX,
          y: mouseEvent.clientY,
        });

        // Adjust position for backward flow (dragged from left handle)
        if (handleId === HANDLE_IDS.LEFT_SOURCE) {
          position = {
            x: position.x - 500, // Node width (400px) + spacing (100px)
            y: position.y,
          };
        }

        // Store data and show quick create dialog
        quickCreateData.current = {
          sourceStepId,
          position,
          handleId,
        };
        setShowQuickCreate(true);
      }

      // Always clear refs
      connectionSourceRef.current = null;
      connectionMadeRef.current = false;
    },
    [screenToFlowPosition],
  );

  /**
   * Validate connections to enforce proper flow direction
   * - Allows: right → left (forward), left-source → right (backward)
   * - Blocks: left-source → left (invalid direction)
   */
  const isValidConnection = useCallback((connection: Connection) => {
    // Block invalid direction: left-source → left
    if (connection.sourceHandle === HANDLE_IDS.LEFT_SOURCE && connection.targetHandle === HANDLE_IDS.LEFT) {
      return false;
    }
    // All other connections are valid
    return true;
  }, []);

  /**
   * Handle quick creation of a new step from drag-and-drop on canvas
   *
   * Supports bidirectional flow creation:
   * - Dragging from RIGHT handle creates forward flow: source → new node
   * - Dragging from LEFT handle creates backward flow: new node → source
   */
  const handleQuickCreate = useCallback(
    (stepType: StepType) => {
      if (!quickCreateData.current) return;

      const { sourceStepId, position, handleId } = quickCreateData.current;
      const { MAX_X, MAX_Y, MIN_X, MIN_Y } = CANVAS_BOUNDS;

      // Constrain position within canvas bounds
      const constrainedPosition = {
        x: Math.max(MIN_X, Math.min(MAX_X, position.x)),
        y: Math.max(MIN_Y, Math.min(MAX_Y, position.y)),
      };

      // Calculate next available column for legacy positioning
      const usedCols = propSteps?.map((s) => s.position.col) || [];
      const maxCol = Math.max(...usedCols, -1);
      const arbitraryCol = Math.min(maxCol + 1, 100);

      const newStep = createNewStep(arbitraryCol, 0, undefined, stepType, propSteps);
      newStep.flowPosition = constrainedPosition;

      // Determine connection direction based on source handle
      let connectionEdge: FunnelEdge;
      if (handleId === HANDLE_IDS.LEFT_SOURCE) {
        // Backward connection: new node → source
        connectionEdge = createEdge(
          newStep.id,
          sourceStepId,
          HANDLE_IDS.RIGHT,
          HANDLE_IDS.LEFT,
          defaultEdgeConditions(stepType),
        );
      } else {
        // Forward connection: source → new node
        const sourceType =
          funnelNodes.find((n) => n.id === sourceStepId)?.type ??
          propSteps?.find((s) => s.id === sourceStepId)?.type;
        connectionEdge = createEdge(
          sourceStepId,
          newStep.id,
          HANDLE_IDS.RIGHT,
          HANDLE_IDS.LEFT,
          defaultEdgeConditions(sourceType),
        );
        newStep.conditions = { from: [sourceStepId] };
      }

      // Update nodes and edges (handles undefined arrays for new funnels)
      if (onUpdateNodes && onUpdateEdges) {
        const { node } = flowStepToNode(newStep);
        onUpdateNodes([...(propNodes || []), node]);
        onUpdateEdges([...(propEdges || []), connectionEdge]);
      } else if (propSteps && onUpdateSteps) {
        onUpdateSteps([...propSteps, newStep]);
      }

      // Select and focus the newly created step
      onStepCreated?.(newStep.id);

      // Enable auto-edit mode for inline label editing
      setNewlyCreatedNodeId(newStep.id);
      setTimeout(() => setNewlyCreatedNodeId(null), 1000);

      quickCreateData.current = null;
    },
    [propNodes, propEdges, funnelNodes, onUpdateNodes, onUpdateEdges, propSteps, onUpdateSteps, onStepCreated],
  );

  // ✨ Handle edge click - open step editor with conditions tab
  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      // 🎯 NEW: Open edge editor when clicking on edge
      if (onEditEdge) {
        onEditEdge(edge.id);
        return;
      }

      // 🔄 LEGACY FALLBACK: Open target node's conditions tab
      const targetNode = funnelNodes.find((n) => n.id === edge.target);
      if (!targetNode) return;

      if (onEditStep) {
        onEditStep(targetNode.id, 'conditions');
      }
    },
    [funnelNodes, onEditStep, onEditEdge],
  );

  // Apply a quick preset condition to the new edge.
  // `null` = dismiss (keep whatever is already on the edge, e.g. the checkout
  // default); `'always'` = explicitly clear the condition.
  const applyQuickCondition = useCallback(
    (conditionKey: string | null) => {
      if (!quickConditionEdge) return;
      const { edgeId } = quickConditionEdge;

      if (conditionKey && onUpdateEdges) {
        const updatedEdges = funnelEdges.map((e) => {
          if (e.id === edgeId) {
            return {
              ...e,
              conditions: { ...e.conditions, when: conditionKey === 'always' ? undefined : conditionKey },
            };
          }
          return e;
        });
        onUpdateEdges(updatedEdges);
      }

      setQuickConditionEdge(null);
    },
    [quickConditionEdge, funnelEdges, onUpdateEdges],
  );

  // Open full edge editor from quick condition popover
  const openFullEdgeEditor = useCallback(() => {
    if (!quickConditionEdge) return;
    const { edgeId } = quickConditionEdge;
    setQuickConditionEdge(null);
    if (onEditEdge) {
      setTimeout(() => onEditEdge(edgeId), 50);
    }
  }, [quickConditionEdge, onEditEdge]);

  // Clicking on canvas background deselects step/edge and closes editors
  const handleBackgroundClick = useCallback(() => {
    if (selectedStepId) {
      onSelectStep(null);
    }
    if (selectedEdgeId && onEditEdge) {
      onEditEdge(null);
    }
  }, [selectedStepId, selectedEdgeId, onSelectStep, onEditEdge]);

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        isValidConnection={isValidConnection}
        onNodeDragStop={onNodeDragStop}
        onEdgeClick={onEdgeClick}
        onPaneClick={handleBackgroundClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 0.9, minZoom: 0.4 }}
        attributionPosition="bottom-left"
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false,
        }}
        minZoom={0.3}
        maxZoom={1.5}
        defaultViewport={{ x: 200, y: 100, zoom: 0.8 }}
        nodeExtent={[
          [CANVAS_BOUNDS.MIN_X, CANVAS_BOUNDS.MIN_Y],
          [CANVAS_BOUNDS.MAX_X + 400, CANVAS_BOUNDS.MAX_Y + 220],
        ]}
        translateExtent={[
          [CANVAS_BOUNDS.MIN_X - 200, CANVAS_BOUNDS.MIN_Y - 200],
          [CANVAS_BOUNDS.MAX_X + 600, CANVAS_BOUNDS.MAX_Y + 420],
        ]}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={true}
        connectionRadius={40}
        connectionLineType={ConnectionLineType.SmoothStep}
        selectNodesOnDrag={false}
        onMove={handleViewportMove}
        onMoveEnd={triggerTourReposition}
        onSelectionChange={triggerTourReposition}
        onNodeClick={(_event, node) => {
          triggerTourReposition();
          // The __workspace_area node spans the whole workspace behind the
          // steps and its wrapper has pointer-events (because a global
          // onNodeClick is set), so it swallows clicks meant for the pane —
          // onPaneClick never fires there. Treat clicks on decorative __
          // nodes (workspace area, boundary markers) as background clicks.
          if (node.id.startsWith('__')) {
            handleBackgroundClick();
          }
        }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="rgba(148, 163, 184, 0.3)"
          className="bg-slate-200/50 dark:bg-slate-900"
        />
        
        {/* 🔒 Read-Only Indicator */}
        {readOnly && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="bg-amber-50/90 backdrop-blur-sm border border-amber-200 text-amber-800 px-4 py-1.5 rounded-full text-xs font-medium shadow-sm flex items-center gap-2 dark:bg-amber-900/50 dark:border-amber-700 dark:text-amber-200">
              <Lock className="w-3.5 h-3.5" />
              <span>{t('flowCanvas.productionReadOnly')}</span>
            </div>
          </div>
        )}

        {/* 🎨 Empty Funnel State */}
        {funnelNodes.length === 0 && !readOnly && (
          <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white/95 px-10 py-8 text-center shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/95">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary shadow-lg">
                <svg className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('flowCanvas.emptyTitle')}</h3>
                <p className="mt-1 max-w-xs text-sm text-slate-500 dark:text-slate-400">
                  {t('flowCanvas.emptyDescription')}
                </p>
              </div>
              <button
                onClick={() => setShowQuickCreate(true)}
                className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                {t('flowCanvas.addFirstStep')}
              </button>
            </div>
          </div>
        )}

        <Controls className="border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900" />
        <MiniMap
          className="border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800"
          nodeColor={(node) => {
            // Hide workspace area and boundary markers in minimap
            if (node.id.startsWith('__')) return 'transparent';

            const nodeData = node.data as FlowStepNodeData | undefined;
            const step = nodeData?.step;
            if (!step) return '#94a3b8';

            const colors: Record<string, string> = {
              landing: '#3b82f6', // blue
              checkout: '#10b981', // green
              offer: '#8b5cf6', // purple
              thankyou: '#ec4899', // pink
              error: '#ef4444', // red
              custom: '#64748b', // slate
              external: '#6366f1', // indigo
            };
            return colors[step.type] || '#94a3b8';
          }}
          nodeStrokeColor={(node) => {
            // Hide workspace area and boundary markers in minimap
            if (node.id.startsWith('__')) return 'transparent';
            return '#e2e8f0';
          }}
          maskColor="rgb(240, 240, 240, 0.6)"
        />
      </ReactFlow>

      {/* ✨ Quick Create Modal */}
      <QuickStepSelectionDialog
        open={showQuickCreate}
        onOpenChange={(open) => {
          setShowQuickCreate(open);
          if (!open) {
            quickCreateData.current = null;
          }
        }}
        onSelectStepType={handleQuickCreate}
        storeType={storeType}
        connectedProviders={connectedProviders}
      />

      {/* ✨ Quick Condition Popover (shown after new connection) */}
      {quickConditionEdge && (() => {
        const qcItems = getQuickConditionsForSource(quickConditionEdge.sourceType);
        return (
          <div className="pointer-events-none absolute inset-0 z-[60]">
            <div
              className="pointer-events-auto absolute"
              style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
            >
              <div className="w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <div className="mb-1.5 flex items-center justify-between px-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('flowCanvas.setCondition')}</span>
                  <button
                    onClick={() => applyQuickCondition(null)}
                    className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="space-y-0.5">
                  {qcItems.map((qc) => {
                    const isApplied = qc.key === quickConditionEdge.appliedKey;
                    return (
                      <button
                        key={qc.key}
                        onClick={() => applyQuickCondition(qc.key)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium transition-colors',
                          isApplied
                            ? 'bg-primary/10 text-primary'
                            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
                        )}
                      >
                        <span className="w-4 text-center text-sm">{qc.icon}</span>
                        {qc.label}
                        {isApplied && (
                          <span className="ml-auto text-[9px] font-semibold uppercase tracking-wider text-primary/70">
                            {t('flowCanvas.defaultCondition')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 border-t border-slate-100 pt-1.5 dark:border-slate-800">
                  <button
                    onClick={openFullEdgeEditor}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-primary transition-colors hover:bg-primary/10 dark:text-primary dark:hover:bg-primary/10"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    {t('flowCanvas.advanced')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
